<script>
  import "carbon-components-svelte/css/g100.css";
  import {
    Dropdown,
    Modal,
    TextInput,
    Tooltip,
  } from "carbon-components-svelte";
  import QrCode32 from "carbon-icons-svelte/lib/QrCode32";
  import WifiController32 from "carbon-icons-svelte/lib/WifiController32";
  import Locked32 from "carbon-icons-svelte/lib/Locked32";
  import Encryption32 from "carbon-icons-svelte/lib/Encryption32";
  import Help16 from "carbon-icons-svelte/lib/Help16";
  import QrCode from "svelte-qrcode";

  const encryptionOptions = [
      { id: "0", text: "WPA", value: "WPA" },
      { id: "1", text: "WEP", value: "WEP" },
      { id: "2", text: "None", value: "" },
    ],
    hiddenOptions = [
      { id: "0", text: "Yes", value: "true" },
      { id: "1", text: "No", value: "false" },
    ],
    ssidPlaceholderOptions = [
      "Drop It Like It's Hotspot",
      "FBI Surveillance Van 2",
      "Tell My WiFi Love Her",
      "Every Day I'm Buffering",
      "hide yo kids, hide yo wifi",
      "The LAN before time",
      "silence_of_the_LANs",
      "no more mister wifi",
      "i_believe_wi_can_fi",
      "It Hurts When IP",
    ],
    passwordPlaceholderOptions = [
      "password123",
      "qwerty",
      "cantguessthis",
      "t0psecret",
      "nick_rocks",
    ];

  let ssid = "",
    password = "",
    encryption = "WPA",
    hidden = "false",
    open = false;

  const onEncryptionSelected = async (event) => {
    const selectedOption = event.detail.selectedItem.value;
    encryption = selectedOption;
  };

  const onHiddenSelected = async (event) => {
    const selectedOption = event.detail.selectedItem.value;
    hidden = selectedOption;
  };

  const getRandomPlaceholder = (options) => {
    return options[Math.floor(Math.random() * options.length)];
  };

  const downloadImage = async () => {
    const qrCodeImg = document.getElementsByClassName("qrcode")[0];
    if (qrCodeImg) {
      const image = await fetch(qrCodeImg.src);
      const imageBlog = await image.blob();
      const imageURL = URL.createObjectURL(imageBlog);

      const link = document.createElement("a");
      link.href = imageURL;
      link.download = ssid + " QR Code.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.error("Could not find QR code image");
    }
  };
</script>

<main>
  <div>
    <header>
      <h1>Easy WiFi Sharing</h1>
      <p>
        Share your WiFi password in seconds! Create a QR code below to allow
        guests to join your WiFi network without typing a long, cryptic
        password. And don't worry, we don't store any of your information. <span
          class="help"
          on:click={() => (open = true)}>Have questions</span
        >?
      </p>
    </header>
    <div class="flex-container">
      <div class="inputs">
        <div class="row">
          <div class="ssid-tooltip">
            <Tooltip tooltipBodyId="ssid-tooltip" icon={Help16}>
              <p id="ssid-tooltip">
                The SSID is the name of your WiFi network. It is the name you
                see when you scan for your network.
              </p>
            </Tooltip>
          </div>
          <TextInput
            labelText="Network Name (SSID)"
            placeholder={getRandomPlaceholder(ssidPlaceholderOptions)}
            size="xl"
            bind:value={ssid}
          />
        </div>
        <div class="row">
          <div class="password-tooltip">
            <Tooltip
              tooltipBodyId="password-tooltip"
              icon={Help16}
              align="start"
            >
              <p id="password-tooltip">
                This is the password you use to connect to your WiFi network. It
                is never stored on our servers.
              </p>
            </Tooltip>
          </div>
          <TextInput
            labelText="Password"
            placeholder={getRandomPlaceholder(passwordPlaceholderOptions)}
            size="xl"
            bind:value={password}
          />
        </div>
        <div class="row split">
          <div class="col">
            <div class="encryption-tooltip">
              <Tooltip
                tooltipBodyId="encryption-tooltip"
                icon={Help16}
                align="start"
              >
                <p id="encryption-tooltip">
                  Most WiFi networks will use WPA or WEP. If you don't know what
                  to choose, try WPA.
                </p>
              </Tooltip>
            </div>
            <Dropdown
              titleText="Encryption"
              size="xl"
              items={encryptionOptions}
              selectedId="0"
              on:select={onEncryptionSelected}
            />
          </div>
          <div class="col">
            <div class="hidden-tooltip">
              <Tooltip tooltipBodyId="hidden-tooltip" icon={Help16} align="end">
                <p id="hidden-tooltip">
                  Do you usually see your WiFi network when you scan for
                  networks? If not, it may be hidden.
                </p>
              </Tooltip>
            </div>
            <Dropdown
              titleText="Hidden Network?"
              size="xl"
              items={hiddenOptions}
              selectedId="1"
              on:select={onHiddenSelected}
            />
          </div>
        </div>
        <div class="row">
          <button class="download" on:click={downloadImage} disabled={!ssid}
            >Download</button
          >
        </div>
      </div>
      <div class="code">
        <div class="qr-code">
          {#if ssid}
            <div class="qr-code-margin">
              <QrCode
                value={"WIFI:S:" +
                  ssid +
                  ";T:" +
                  encryption +
                  ";P:" +
                  password +
                  ";H:" +
                  hidden +
                  ";"}
                size="290"
              />
            </div>
          {:else}
            <div class="qr-code-placeholder" />
          {/if}
        </div>
      </div>
    </div>

    <Modal
      passiveModal
      bind:open
      modalHeading="Let's answer those questions"
      on:open
      on:close
    >
      <div class="icon">
        <QrCode32 />
      </div>
      <h4>What is this?</h4>
      <p>
        It's a QR code generator. QR codes are an easy way to share snippets of
        text like a web address. They can *also* be used to share information
        about your WiFi network and allow others to connect automatically.
      </p>
      <ol>
        <p>Here's how it works:</p>
        <li>
          1. Provide the network name, password, and encryption type for your
          WiFi network
        </li>
        <li>
          2. Download the QR code and share it with your guests (for example,
          you may choose to print it and leave it on the refrigerator)
        </li>
        <li>
          3. Guests then scan the QR code with their device's camera and connect
          to your WiFi network -- just like magic!
        </li>
      </ol>

      <div class="icon">
        <Locked32 />
      </div>
      <h4>Is this secure?</h4>
      <p>
        Yes. We never store your WiFi password. In fact, we never store any of
        your information. The QR code is generated on your device and not our
        server.
      </p>

      <div class="icon">
        <Encryption32 />
      </div>
      <h4>Which encryption option should I choose?</h4>
      <p>
        If you're not sure, start with WPA (WiFi Protected Access protocol). Use
        this option for WPA2 and WPA3 as well. This will be the standard for
        most WiFi networks.
      </p>

      <div class="icon">
        <WifiController32 />
      </div>
      <h4>What is an "SSID"?</h4>
      <p>
        It stands for service set identifier. This is the name of your wireless
        network (e.g., "pablo's wifi").
      </p>
    </Modal>
  </div>
</main>
<footer>
  Nick made this. <a href="https://twitter.com/nickflynndev" target="_blank"
    >Say hello.</a
  > 👋
</footer>

<style>
  main {
    max-width: 760px;
    margin: 30px auto 0;

    flex: 1 0 auto;
  }

  h1 {
    padding-bottom: 10px;
  }

  header {
    padding: 0 39px;
    margin: 0 0 50px 0;
  }

  p {
    padding: 0 !important;
  }

  ol {
    margin-top: 10px;
  }

  .icon {
    margin-top: 25px;
    color: #0f62fe;
  }

  .ssid-tooltip,
  .password-tooltip,
  .encryption-tooltip,
  .hidden-tooltip {
    position: absolute;
  }

  .ssid-tooltip {
    left: 135px;
  }

  .password-tooltip {
    left: 66px;
  }

  .encryption-tooltip {
    left: 71px;
  }

  .hidden-tooltip {
    right: 48px;
  }

  .help {
    cursor: pointer;
    font-weight: bold;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-decoration-color: #555;
    color: #0f62fe;
  }

  .flex-container {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
    height: 100%;
  }

  .inputs {
    width: 50%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-end;
  }

  .code {
    width: 50%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  }

  .row {
    width: 350px;
    padding: 0 10px;
    position: relative;
  }

  .row:not(:first-child) {
    margin-top: 15px;
  }

  .split {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: left;
    flex-wrap: wrap;
  }

  .col {
    width: calc(50% - 5px);
  }

  .col:first-child {
    margin-right: 5px;
  }

  .col:last-child {
    margin-left: 5px;
  }

  .download {
    margin-top: 20px;
    width: 100%;
  }

  .qr-code {
    width: 330px;
    height: 330px;

    background-color: #fff;
    margin-left: 15px;
  }

  .qr-code-margin {
    width: 290px;
    margin: 20px auto;
  }

  .qr-code-placeholder {
    width: 290px;
    height: 290px;
    background-color: #eee;
    margin: 20px auto;
  }

  footer {
    flex-shrink: 0;
    padding-bottom: 25px;
    margin: 30px auto 0;
  }

  @media only screen and (max-width: 760px) {
    header {
      max-width: 330px;
      margin: 0 auto 30px;
      padding: 0;
    }

    .flex-container {
      flex-direction: column;
    }

    .inputs,
    .code {
      align-items: center;
    }

    .download {
      margin: 10px 0;
    }

    .qr-code {
      margin: 15px 0 0 0;
    }
  }
</style>
